import { DiwaAPI } from './client'

/** Mirrors POST /auth/login and /auth/signup in products/diwa/src/diwa/auth/router.py */
export interface TokenResponse {
  access_token: string
  token_type: string
}

export interface AuthUser {
  id: string
  email: string
  email_verified: boolean
}

/** POST /auth/signup — echoes the normalized plan the account is headed for and
 *  whether it still needs a paid checkout to take effect (see auth/router.py). */
export interface SignupResult extends AuthUser {
  plan: string
  checkout_required: boolean
}

/** POST /auth/forgot-password — a neutral, non-enumerating acknowledgement.
 *  Currently always `unavailable` (email infra pending). */
export interface ForgotPasswordResult {
  status: string
  message: string
}

/** GET /auth/gate/status — is "Sign in with your Diwata ID" on, and where does
 *  the gate live? gate_origin relays the backend's single GATE_PUBLIC_ORIGIN
 *  config value (one_login_spec.md §3) — the SPA never hardcodes a gate host. */
export interface GateStatus {
  enabled: boolean
  gate_origin: string | null
  client_id: string | null
  /** Pre-launch registration gate (DIWA_REGISTRATION_GATED, relayed). False =
   *  waitlist-first: signup needs an access code and every "create account"
   *  surface tells the waitlist story. Absent (older backend) reads as closed
   *  on marketing surfaces — the safe pre-launch default. */
  registration_open?: boolean
}

export class AuthAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  signup(
    email: string,
    password: string,
    displayName?: string,
    plan?: string,
    accessCode?: string,
  ): Promise<SignupResult> {
    return this.api.post<SignupResult>('/auth/signup', {
      email,
      password,
      ...(displayName ? { display_name: displayName } : {}),
      ...(plan ? { plan } : {}),
      // Early-access code: required (and spent) server-side while registration
      // is gated; harmlessly ignored once the doors are open.
      ...(accessCode ? { access_code: accessCode } : {}),
    })
  }

  login(email: string, password: string): Promise<TokenResponse> {
    return this.api.post<TokenResponse>('/auth/login', { email, password })
  }

  forgotPassword(email: string): Promise<ForgotPasswordResult> {
    return this.api.post<ForgotPasswordResult>('/auth/forgot-password', { email })
  }

  me(): Promise<AuthUser> {
    return this.api.get<AuthUser>('/auth/me')
  }

  gateStatus(): Promise<GateStatus> {
    return this.api.get<GateStatus>('/auth/gate/status')
  }

  /** Redeem the Gate's one-time code (server-side, via the diwa backend) for an
   *  ordinary diwa session token — the same shape /auth/login returns. */
  gateExchange(code: string, redirectUri: string): Promise<TokenResponse> {
    return this.api.post<TokenResponse>('/auth/gate/exchange', {
      code,
      redirect_uri: redirectUri,
    })
  }
}
